import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './TournamentBracketTree.module.css';

interface Mercenary {
  name: string;
}

interface Match {
  id: number;
  team1Participants: Mercenary[];
  team2Participants: Mercenary[];
  winner_team?: number;
  round_number: number;
  battle_report?: number;
  match_type?: 'normal' | 'final' | 'third_place';
}

interface BracketTreeProps {
  matches: Match[];
  numTeams: number;
}

interface BracketMatch {
  match: Match | null;
  position: { x: number; y: number };
  isThirdPlace?: boolean;
}

const TournamentBracketTree: React.FC<BracketTreeProps> = ({ matches, numTeams }) => {
  const [scale, setScale] = useState(0.8); // Start slightly zoomed out to show more
  const [position, setPosition] = useState({ x: 0, y: -200 }); // Start with view shifted up to show more matches
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      // Adjust initial scale and position for mobile
      if (window.innerWidth <= 768) {
        setScale(0.5);
        setPosition({ x: 50, y: -100 });
      } else {
        setScale(0.8);
        setPosition({ x: 0, y: -200 });
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate bracket structure
  const calculateBracketStructure = (): BracketMatch[] => {
    const bracketMatches: BracketMatch[] = [];
    const rounds = Math.ceil(Math.log2(numTeams));
    
    // Group matches by round
    const matchesByRound: { [key: number]: Match[] } = {};
    matches.forEach(match => {
      if (!matchesByRound[match.round_number]) {
        matchesByRound[match.round_number] = [];
      }
      matchesByRound[match.round_number].push(match);
    });

    // Simple positioning with reasonable spacing
    const ROUND_SPACING = 350;
    const MATCH_SPACING = 220; // Increased spacing to accommodate taller match cards (180px height + 40px gap)
    const START_Y = 50;

    for (let round = 1; round <= rounds; round++) {
      const roundMatches = matchesByRound[round] || [];
      const roundX = 50 + (round - 1) * ROUND_SPACING;

      // Each round starts from the same Y position and stacks its matches
      roundMatches.forEach((match, index) => {
        bracketMatches.push({
          match,
          position: {
            x: roundX,
            y: START_Y + (index * MATCH_SPACING)
          },
          isThirdPlace: match.match_type === 'third_place'
        });
      });
    }

    return bracketMatches;
  };

  const bracketStructure = calculateBracketStructure();

  // Calculate connecting lines
  const calculateConnections = (): JSX.Element[] => {
    const connections: JSX.Element[] = [];
    const rounds = Math.ceil(Math.log2(numTeams));

    for (let round = 1; round < rounds; round++) {
      const currentRoundMatches = bracketStructure.filter(bm =>
        bm.match && bm.match.round_number === round &&
        (bm.match.match_type === 'normal' || !bm.match.match_type)
      );
      const nextRoundMatches = bracketStructure.filter(bm =>
        bm.match && bm.match.round_number === round + 1 &&
        (bm.match.match_type === 'normal' || !bm.match.match_type)
      );

      // Connect pairs of current round matches to next round matches
      for (let i = 0; i < nextRoundMatches.length; i++) {
        const match1 = currentRoundMatches[i * 2];
        const match2 = currentRoundMatches[i * 2 + 1];
        const nextMatch = nextRoundMatches[i];

        if (match1 && match2 && nextMatch) {
          const x1 = match1.position.x + 220; // End of first match
          const y1 = match1.position.y + 70;  // Center of first match
          const x2 = match2.position.x + 220; // End of second match
          const y2 = match2.position.y + 70;  // Center of second match
          const x3 = nextMatch.position.x;    // Start of next match
          const y3 = nextMatch.position.y + 70; // Center of next match

          const midX = x1 + 30; // Connection point

          // Create connecting lines
          connections.push(
            <g key={`connection-${round}-${i}`}>
              {/* Horizontal line from match1 */}
              <line x1={x1} y1={y1} x2={midX} y2={y1} />
              {/* Horizontal line from match2 */}
              <line x1={x2} y1={y2} x2={midX} y2={y2} />
              {/* Vertical connecting line */}
              <line x1={midX} y1={y1} x2={midX} y2={y2} />
              {/* Horizontal line to next match */}
              <line x1={midX} y1={y3} x2={x3} y2={y3} />
            </g>
          );
        }
      }
    }

    return connections;
  };

  // Handle zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.3, Math.min(3, prev * delta)));
  };

  // Handle pan with improved performance - unified for mouse and touch
  const handleStart = (clientX: number, clientY: number) => {
    setIsDragging(true);
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  const handleMove = (clientX: number, clientY: number) => {
    if (isDragging) {
      let newX = clientX - dragStart.x;
      let newY = clientY - dragStart.y;

      // Adjust bounds for mobile vs desktop
      const bounds = isMobile
        ? { maxX: 100, minX: -800, maxY: 50, minY: -300 }
        : { maxX: 200, minX: -1000, maxY: 100, minY: -400 };

      newX = Math.max(bounds.minX, Math.min(bounds.maxX, newX));
      newY = Math.max(bounds.minY, Math.min(bounds.maxY, newY));

      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        setPosition({ x: newX, y: newY });
      });
    }
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    }
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  const handleMouseLeave = () => {
    handleEnd();
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    handleEnd();
  };

  // Reset view - adjust for mobile
  const resetView = () => {
    if (isMobile) {
      setScale(0.5);
      setPosition({ x: 50, y: -100 });
    } else {
      setScale(0.8);
      setPosition({ x: 0, y: -200 });
    }
  };

  const renderMatch = (bracketMatch: BracketMatch) => {
    const { match, position: pos, isThirdPlace } = bracketMatch;
    
    if (!match) return null;

    const team1Names = match.team1Participants.map(p => p.name).join(', ');
    const team2Names = match.team2Participants.map(p => p.name).join(', ');

    return (
      <div
        key={match.id}
        className={`${styles.match} ${isThirdPlace ? styles.thirdPlaceMatch : ''}`}
        style={{
          left: pos.x,
          top: pos.y,
          position: 'absolute'
        }}
      >
        {match.match_type === 'final' && (
          <div className={styles.finalLabel}>Final</div>
        )}
        {isThirdPlace && (
          <div className={styles.thirdPlaceLabel}>3rd Place</div>
        )}
        
        <div className={`${styles.team} ${match.winner_team === 1 ? styles.winnerTeam : ''}`}>
          {team1Names || 'TBD'}
        </div>
        
        <div className={styles.vs}>VS</div>
        
        <div className={`${styles.team} ${match.winner_team === 2 ? styles.winnerTeam : ''}`}>
          {team2Names || 'TBD'}
        </div>

        {match.winner_team && (
          <div className={styles.winner}>
            Winner: {match.winner_team === 1 ? team1Names : team2Names}
          </div>
        )}

        {match.battle_report && (
          <Link
            to={`/battle-report/${match.battle_report}`}
            className={styles.viewBattleReportLink}
          >
            View Battle Report
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className={styles.bracketContainer}>
      <div className={styles.controls}>
        <button onClick={() => setScale(prev => Math.min(3, prev * 1.2))}>Zoom In</button>
        <button onClick={() => setScale(prev => Math.max(0.3, prev * 0.8))}>Zoom Out</button>
        <button onClick={resetView}>Reset View</button>
        <span className={styles.scaleIndicator}>Scale: {Math.round(scale * 100)}%</span>
        <span className={styles.hint}>Drag to pan around the bracket</span>
      </div>
      
      <div
        ref={containerRef}
        className={styles.bracketViewport}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: 'none' // Prevent default touch behaviors
        }}
      >
        <div
          className={styles.bracketContent}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '0 0'
          }}
        >
          {/* Connection lines removed for cleaner bracket view */}

          {bracketStructure.map((bracketMatch, index) => renderMatch(bracketMatch))}
        </div>
      </div>
    </div>
  );
};

export default TournamentBracketTree;
